import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Field, SimpleSelect } from "@/components/common";
import { MONTHS } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileUp, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { fmtTanggal, alasanCodeBlueText } from "@/lib/siport";

const NONE = "__none__";

export default function SiportImport() {
  const navigate = useNavigate();
  const now = new Date();
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [poli, setPoli] = useState(NONE);
  const [polis, setPolis] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [onlyProblem, setOnlyProblem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/siport/form-options").then((r) => setPolis(r.data.poliklinik));
  }, []);

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  const doPreview = async () => {
    if (!file) return toast.error("Pilih file Excel rekap lama terlebih dahulu");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("bulan", bulan);
    fd.append("tahun", tahun);
    if (poli !== NONE) fd.append("poliklinik_id", poli);
    setLoading(true);
    try {
      const r = await api.post("/siport/import/preview", fd);
      setPreview(r.data);
      if (!r.data.total_baris) toast.warning("Tidak ada baris data yang terbaca");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await api.post("/siport/import/confirm", {
        nama_file: preview.nama_file,
        rows: preview.rows.map((x) => ({ baris: x.baris, data: x.data })),
      });
      toast.success(r.data.message);
      navigate("/siport/data");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const rows = (preview?.rows || []).filter((r) => !onlyProblem || r.masalah.length);

  return (
    <div data-testid="siport-import-page">
      <PageHeader icon={FileUp} title="Import Data Lama SIPORT"
        subtitle="Masukkan rekap Excel manual (format kolom rekap lama). Data dinormalisasi otomatis dan ditampilkan untuk dicek sebelum disimpan." />

      <Card className="border-slate-200 shadow-sm mb-6 max-w-4xl">
        <CardHeader>
          <CardTitle className="text-lg">1. Pilih file & default</CardTitle>
          <CardDescription>
            Rekap lama tidak punya tanggal & poliklinik, jadi pilih bulan/tahun default (tanggal diisi tgl 1 bulan tsb).
            Baris bermasalah tetap bisa diimport dan akan ditandai “perlu dicek”.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Field label="Bulan default">
              <SimpleSelect value={bulan} onChange={setBulan} className="w-[150px]"
                options={MONTHS.map((m) => ({ label: m.nama, value: m.n }))} />
            </Field>
            <Field label="Tahun default">
              <SimpleSelect value={tahun} onChange={setTahun} className="w-[110px]"
                options={years.map((y) => ({ label: String(y), value: y }))} />
            </Field>
            <Field label="Poliklinik (opsional)">
              <SimpleSelect value={poli} onChange={setPoli} className="w-[240px]"
                options={[{ label: "Tidak tercatat", value: NONE }, ...polis.map((p) => ({ label: p.nama, value: p.id }))]} />
            </Field>
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary hover:bg-brand-mist/40 transition-colors"
          >
            <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-700 font-medium">{file ? file.name : "Klik untuk memilih file .xlsx"}</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="siport-import-file"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          </div>
          <Button onClick={doPreview} disabled={loading || !file} data-testid="siport-import-preview">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Baca & Preview
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="text-lg">2. Preview — {preview.nama_file}</CardTitle>
              <CardDescription className="flex flex-wrap gap-2 mt-2">
                <Badge variant="secondary">{preview.total_baris} baris</Badge>
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  <CheckCircle2 className="w-3 h-3 mr-1" />{preview.total_baris - preview.jumlah_bermasalah} bersih
                </Badge>
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                  <AlertTriangle className="w-3 h-3 mr-1" />{preview.jumlah_bermasalah} perlu dicek
                </Badge>
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOnlyProblem((v) => !v)}>
                {onlyProblem ? "Tampilkan semua" : "Hanya yang bermasalah"}
              </Button>
              <Button onClick={confirm} disabled={saving || !preview.total_baris} data-testid="siport-import-confirm">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Import {preview.total_baris} laporan
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">Kolom terdeteksi: {preview.kolom_terdeteksi.join(", ")}</p>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Baris</TableHead>
                    <TableHead>Pasien</TableHead>
                    <TableHead>Usia</TableHead>
                    <TableHead>No. RM</TableHead>
                    <TableHead>Tanggal Input</TableHead>
                    <TableHead>EWS</TableHead>
                    <TableHead>Tindak Lanjut</TableHead>
                    <TableHead>Code Blue</TableHead>
                    <TableHead>Masalah</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const d = r.data;
                    return (
                      <TableRow key={r.baris} className={r.masalah.length ? "bg-amber-50/60" : ""}>
                        <TableCell className="tabular-nums">{r.baris}</TableCell>
                        <TableCell className="font-medium">{d.nama_pasien}</TableCell>
                        <TableCell className="tabular-nums">{d.usia ?? "—"}</TableCell>
                        <TableCell className="tabular-nums whitespace-nowrap">{d.no_rm || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">{fmtTanggal(d.tanggal_kejadian)}</TableCell>
                        <TableCell className="whitespace-nowrap">{d.ews_score?.label?.split(" (")[0] || "—"}</TableCell>
                        <TableCell>{d.tindak_lanjut?.label || "—"}</TableCell>
                        <TableCell className="text-xs max-w-[220px]">
                          {d.code_blue ? (
                            <>
                              <div>{alasanCodeBlueText(d.code_blue) || "—"}</div>
                              <div className="tabular-nums text-slate-500">
                                {d.code_blue.jam_aktivasi || "?"} → {d.code_blue.jam_tmrc_datang || "?"} ·{" "}
                                {d.code_blue.respon_time_menit != null ? `${d.code_blue.respon_time_menit} mnt` : "respon ?"}
                              </div>
                            </>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-amber-800 max-w-[280px]">
                          {r.masalah.length ? <ul className="list-disc pl-4">{r.masalah.map((m, i) => <li key={i}>{m}</li>)}</ul>
                            : <span className="text-emerald-700">OK</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
